import type { QuoteResponse } from '../types/quote-types'
import { decryptAndParseJson } from '../util/crypto'
import {
  BUYONLINE_QUOTE_URL,
  BUYONLINE_INITIAL_URL,
  BUYONLINE_EMAIL_URL,
  AGENT_SESSION_URL,
  VISION_RATES_API,
  DENTAL_RATES_API,
} from '../util/api'
import { getEmbeddedContext } from '../util/embedded'
import type {
  QuoteRequest,
  InitialForms,
  EmailForms,
} from '../types/quote-types'
export type { QuoteResponse, QuoteRequest } from '../types/quote-types'
import type {
  RatesRequest,
  VisionRatesResponse,
  DentalRatesResponse,
} from '../types/rates-types'

const isDev = import.meta.env.DEV || import.meta.env.MODE === 'development'

export class QuoteService {
  private static agentForms: InitialForms | null = null
  private static quoteInProgress: Promise<QuoteResponse> | null = null

  static cacheAgentForms(forms: InitialForms | null) {
    this.agentForms = forms || null
  }

  static getCachedAgentForms(): InitialForms | null {
    return this.agentForms
  }

  private static normalizePhone(value?: string): string {
    return (value || '').replace(/\D/g, '')
  }

  static async getProducerSessionDetails(): Promise<Record<
    string,
    any
  > | null> {
    try {
      // Use sample json response when running on localhost for development.
      if (typeof window !== 'undefined') {
        const host = window.location.hostname
        if (host === 'localhost' || host === '127.0.0.1') {
          if (isDev) {
            console.log(
              '[QuoteService] Using sample producer data for localhost'
            )
          }
          const sampleUrl = new URL(
            '../fixtures/producer.json',
            import.meta.url
          )
          const sampleResp = await fetch(sampleUrl)
          if (!sampleResp.ok) return null
          return await sampleResp.json()
        }
      }
      // Prefer explicit producer number from URL params: ?pnumber=XXXXX
      // If no pnum is found, default to id=0
      let url = AGENT_SESSION_URL()
      try {
        if (typeof window !== 'undefined') {
          const pnum = new URLSearchParams(window.location.search).get(
            'pnumber'
          )
          if (pnum) {
            url = `${AGENT_SESSION_URL()}?id=${encodeURIComponent(pnum)}`
          }
        }
      } catch {
        // fallback to AGENT_SESSION_URL
      }

      if (isDev) {
        console.log('[QuoteService] Fetching producer session from:', url)
      }

      const resp = await fetch(url, {
        method: 'GET',
      })
      if (!resp.ok) {
        throw new Error(`Agent session request failed: ${resp.statusText}`)
      }

      // Session details endpoint returns JSON
      return await resp.json()
    } catch (e) {
      console.error('Failed to fetch producer session details:', e)
      return null
    }
  }

  static async getQuote(
    request: QuoteRequest,
    producerSession?: Record<string, any> | null
  ): Promise<QuoteResponse> {
    // If a request is already in progress, return the same promise
    if (this.quoteInProgress) {
      if (isDev) {
        console.warn(
          '[QuoteService] getQuote already in progress, returning existing promise'
        )
      }
      return this.quoteInProgress
    }

    if (isDev) {
      console.log('[QuoteService] Starting getQuote request')
    }

    // Store the promise so duplicate callers get the same result
    this.quoteInProgress = this.executeGetQuote(request, producerSession)

    try {
      const result = await this.quoteInProgress
      return result
    } finally {
      this.quoteInProgress = null
    }
  }

  private static async executeGetQuote(
    request: QuoteRequest,
    producerSession?: Record<string, any> | null
  ): Promise<QuoteResponse> {
    try {
      // If this is an agent-driven quote, enrich request with producer session details
      const { psaleIsY, isEmbedded, pnumber } = getEmbeddedContext()

      if (request.isAgent && (psaleIsY || isEmbedded)) {
        let producerFromForms: Record<string, any> | null = null
        try {
          const forms = QuoteService.getCachedAgentForms()
          const pinfo = forms?.producerInfoJ
          producerFromForms = pinfo ? decryptAndParseJson(pinfo) : null
        } catch {
          producerFromForms = null
        }

        const session = producerSession || null

        const producerPNumber =
          session?.producerPNumber ||
          session?.producerNumber ||
          producerFromForms?.producerPNumber ||
          producerFromForms?.producerNumber ||
          pnumber ||
          ''
        const producerEmail =
          session?.email || producerFromForms?.email || request.email || ''
        const producerPhone = this.normalizePhone(
          session?.producerPhone ||
            session?.workPhoneNumber ||
            session?.phone ||
            producerFromForms?.producerPhone ||
            producerFromForms?.workPhoneNumber ||
            producerFromForms?.phone ||
            ''
        )
        const fullName =
          `${
            session?.firstName ||
            producerFromForms?.firstName ||
            request.firstName ||
            ''
          } ${
            session?.lastName ||
            producerFromForms?.lastName ||
            request.lastName ||
            ''
          }`.trim() ||
          session?.fullName ||
          producerFromForms?.fullName ||
          request.fullName ||
          ''

        request.producerPNumber = request.producerPNumber || producerPNumber
        request.producerEmail = request.producerEmail || producerEmail
        request.producerPhone = request.producerPhone || producerPhone
        request.fullName = request.fullName || fullName
        request.corpName =
          request.corpName ||
          producerFromForms?.individualAgencyName ||
          producerFromForms?.corpName ||
          session?.individualAgencyName ||
          session?.corpName ||
          ''

        if (isEmbedded && !request.deliveryMethod) {
          request.deliveryMethod = 'p'
        }

        request.producerSale = request.producerSale || (psaleIsY ? 'y' : 'n')
      }

      if (isDev) {
        console.log('Submitting quote request:', request)
      }

      // Base64 encode the request payload
      const quoteForm = btoa(JSON.stringify(request))

      // Keep objectEncode stateless to avoid session/context bleed.
      const objectEncode: any = 'undefined'
      if (request.isAgent && isDev) {
        console.warn(
          '[QuoteService] objectEncode cache disabled; sending undefined objectEncode'
        )
      }

      // Wrap in serializedForm structure to match Angular implementation
      const serializedForm = JSON.stringify({
        quoteForm,
        objectEncode,
      })

      // Use URLSearchParams to match Angular's HttpParams format
      const params = new URLSearchParams({
        serializedForm: serializedForm,
      })

      if (isDev) {
        console.log(
          '[QuoteService] Sending quote request to:',
          BUYONLINE_QUOTE_URL()
        )
      }

      const response = await fetch(BUYONLINE_QUOTE_URL(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: params.toString(),
      })

      if (!response.ok) {
        throw new Error(`Quote request failed: ${response.statusText}`)
      }

      const responseText = await response.text()

      try {
        // First try parsing as JSON to get the encrypted string
        const parsedResponse = JSON.parse(responseText)

        // If it's a string, it's likely the encrypted data
        let finalData: any
        if (typeof parsedResponse === 'string') {
          finalData = decryptAndParseJson(parsedResponse)
        } else {
          // If it's already an object, use it
          finalData = parsedResponse
        }

        // Dev-only: log a compact summary to help diagnose missing results
        if (isDev) {
          console.log('Quote response:', finalData)
          const plansCount = Array.isArray(finalData?.individualPlans)
            ? finalData.individualPlans.length
            : 0
          const dentalCount = Array.isArray(finalData?.crossSalePlans)
            ? finalData.crossSalePlans.length
            : Array.isArray(finalData?.jsonPlans)
              ? finalData.jsonPlans.length
              : 0
          const status = finalData?.localStatus || 'unknown'
          const view = finalData?.lastViewRequested || 'unknown'
          console.log('[QuoteService] Response summary:', {
            status,
            view,
            plansCount,
            dentalCount,
            hasQuoteApplication: !!finalData?.quoteApplication,
          })
        }

        return finalData
      } catch (parseError) {
        console.error('Failed to parse quote response:', parseError)
        throw parseError
      }
    } catch (error) {
      console.error('Error submitting quote:', error)
      throw error
    }
  }

  /**
   * Fetch agent initialization data to obtain encoded `authorizationToken` and `producerInfoJ`.
   * Returns the `forms` object if present in the response.
   */
  static async getInitialForms(
    producerNumber: string
  ): Promise<InitialForms | null> {
    try {
      const pnum = producerNumber || ''
      const url = `${BUYONLINE_INITIAL_URL()}?pnumber=${encodeURIComponent(pnum)}&psale=y`

      if (isDev) {
        console.log('[QuoteService] Fetching initial forms from:', url)
      }

      // Note: Initial forms call doesn't need Authorization header
      // The authorizationToken is returned in the response
      const resp = await fetch(url, {
        method: 'GET',
      })
      if (!resp.ok) {
        throw new Error(`Initial forms request failed: ${resp.statusText}`)
      }

      const responseText = await resp.text()
      try {
        const parsed = JSON.parse(responseText)
        const data =
          typeof parsed === 'string' ? decryptAndParseJson(parsed) : parsed
        const forms = data?.forms ?? null
        // Cache for later use in email flows
        QuoteService.cacheAgentForms(forms)
        return forms
      } catch (e) {
        // Non-JSON response; nothing to extract
        if (isDev) {
          console.warn(
            'Initial forms response was not JSON; unable to parse forms'
          )
        }
        return null
      }
    } catch (error) {
      console.error('Error fetching initial forms:', error)
      // Clear cache on failure
      QuoteService.cacheAgentForms(null)
      return null
    }
  }

  /**
   * Retrieve an existing quote by `quoteId` via initial action. Applies agent recommendations
   * from `quoteApplication.quotedPlan` and `quoteApplication.selectedCrossSalePlans` onto the
   * returned `individualPlans` and `crossSalePlans/jsonPlans` arrays.
   */
  static async getInitialQuoteById(quoteId: string): Promise<QuoteResponse> {
    const url = `${BUYONLINE_INITIAL_URL()}?quoteId=${encodeURIComponent(quoteId)}`
    const resp = await fetch(url, {
      method: 'GET',
    })
    if (!resp.ok) {
      throw new Error(`Initial quote request failed: ${resp.statusText}`)
    }
    const responseText = await resp.text()
    // Decrypt/parse response similar to getQuote()
    let data: any
    try {
      const parsedResponse = JSON.parse(responseText)
      if (typeof parsedResponse === 'string') {
        data = decryptAndParseJson(parsedResponse)
      } else {
        data = parsedResponse
      }
    } catch (e) {
      // Fallback: attempt to decrypt raw text if JSON parse failed
      try {
        data = decryptAndParseJson(responseText)
      } catch {
        throw e
      }
    }

    if (isDev) {
      console.log('Initial quote data from outlook email:', data)
    }

    // Apply agent recommendations to plan arrays
    try {
      const qApp = data?.quoteApplication || data?.quoteQApplication
      const quotedVision = qApp?.quotedPlan
      const selectedDental = qApp?.selectedCrossSalePlans?.dental

      // Vision recommendation
      if (
        quotedVision?.isAgentRecommendedPlan === true &&
        quotedVision?.planCode
      ) {
        const code = quotedVision.planCode
        const visionPlans: any[] = Array.isArray(data?.individualPlans)
          ? data.individualPlans
          : []
        for (const vp of visionPlans) {
          if (vp?.id === code || vp?.planId === code) {
            vp.producerRecommended = true
          }
        }
      }

      // Dental recommendation: prefer selectedCrossSalePlans, but also handle dental-only quotes via quotedPlan
      if (
        selectedDental?.isAgentRecommendedPlan === true &&
        selectedDental?.planCode
      ) {
        const code = selectedDental.planCode
        const dentalPlans: any[] = Array.isArray(data?.crossSalePlans)
          ? data.crossSalePlans
          : Array.isArray(data?.jsonPlans)
            ? data.jsonPlans
            : []
        for (const dp of dentalPlans) {
          if (dp?.planId === code || dp?.id === code) {
            dp.producerRecommended = true
          }
        }
      } else if (
        quotedVision?.isAgentRecommendedPlan === true &&
        quotedVision?.planCode &&
        String(quotedVision?.productType || '').toUpperCase() === 'DENTAL'
      ) {
        const code = quotedVision.planCode
        const dentalPlans: any[] = Array.isArray(data?.crossSalePlans)
          ? data.crossSalePlans
          : Array.isArray(data?.jsonPlans)
            ? data.jsonPlans
            : []
        for (const dp of dentalPlans) {
          if (dp?.planId === code || dp?.id === code) {
            dp.producerRecommended = true
          }
        }
      }
    } catch {
      // non-blocking
    }

    // Auto-sort: move recommended plans to the top while preserving relative order
    try {
      const isRecommended = (p: any) => {
        const r1 = p?.producerRecommended
        const r2 = p?.recommended
        return (
          r1 === true ||
          r1 === 'y' ||
          r1 === 'Y' ||
          r2 === true ||
          r2 === 'y' ||
          r2 === 'Y'
        )
      }
      const sortRecommendedFirst = (arr: any[] | undefined) => {
        if (!Array.isArray(arr)) return arr
        const rec: any[] = []
        const other: any[] = []
        for (const p of arr) {
          ;(isRecommended(p) ? rec : other).push(p)
        }
        return [...rec, ...other]
      }
      data.individualPlans = sortRecommendedFirst(data.individualPlans)
      data.crossSalePlans = sortRecommendedFirst(data.crossSalePlans)
      data.jsonPlans = sortRecommendedFirst(data.jsonPlans)
    } catch {
      // non-blocking
    }

    // Cache forms (including producerInfoJ) for agent banner use
    if (data?.forms) {
      QuoteService.cacheAgentForms(data.forms)
    }

    return data as QuoteResponse
  }

  static async sendEmailNotice(
    notice: { ccAgent: boolean; message: string },
    producerNumber: string | null | undefined,
    agentRecommendedPlans: Array<any>,
    objectEncode: any,
    forms?: EmailForms
  ): Promise<any> {
    try {
      const pnum = producerNumber || ''
      const url = `${BUYONLINE_EMAIL_URL()}?pnumber=${encodeURIComponent(pnum)}&psale=y`

      const finalObjectEncode = objectEncode || 'undefined'

      // Prepare payload with message, ccAgent, agentRecommendedPlans, and full quote object as objectEncode
      const serializedForm = JSON.stringify({
        message: notice.message,
        ccAgent: notice.ccAgent,
        agentRecommendedPlans,
        offerCrossSale: true,
        objectEncode: finalObjectEncode,
        ...(forms
          ? {
              forms: {
                authorizationToken: forms.authorizationToken,
                producerInfoJ: forms.producerInfoJ,
                quoteForm: forms.quoteForm,
              },
            }
          : {}),
      })
      if (isDev) {
        console.log('Sending email notice with payload:', serializedForm)
      }
      const params = new URLSearchParams({ serializedForm })

      const resp = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: params.toString(),
      })

      if (!resp.ok) {
        throw new Error(`Email notice request failed: ${resp.statusText}`)
      }

      const responseText = await resp.text()

      try {
        const parsed = JSON.parse(responseText)
        if (typeof parsed === 'string') {
          if (isDev) {
            console.log(
              'Decrypting email notice response' +
                JSON.stringify(decryptAndParseJson(parsed))
            )
          }
          return decryptAndParseJson(parsed)
        }
        return parsed
      } catch {
        // If not JSON, return raw text
        return responseText
      }
    } catch (error) {
      console.error('Error sending email notice:', error)
      throw error
    }
  }

  /**
   * Fetch vision rates for immediate-binder flow
   */
  static async getVisionRates(
    request: RatesRequest
  ): Promise<VisionRatesResponse> {
    try {
      const clientId =
        import.meta.env.VITE_DEFAULT_X_IBM_CLIENT_ID != null
          ? import.meta.env.VITE_DEFAULT_X_IBM_CLIENT_ID
          : 'default-client-id'
      const response = await fetch(VISION_RATES_API(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-IBM-Client-ID': clientId,
        },
        body: JSON.stringify(request),
      })

      if (!response.ok) {
        throw new Error(`Vision rates request failed: ${response.statusText}`)
      }

      return await response.json()
    } catch (error) {
      console.error('Error fetching vision rates:', error)
      throw error
    }
  }

  /**
   * Fetch dental rates for immediate-binder flow
   */
  static async getDentalRates(
    request: RatesRequest
  ): Promise<DentalRatesResponse> {
    try {
      const clientId =
        import.meta.env.VITE_DEFAULT_X_IBM_CLIENT_ID != null
          ? import.meta.env.VITE_DEFAULT_X_IBM_CLIENT_ID
          : 'default-client-id'
      const response = await fetch(DENTAL_RATES_API(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-IBM-Client-ID': clientId,
        },
        body: JSON.stringify(request),
      })

      if (!response.ok) {
        throw new Error(`Dental rates request failed: ${response.statusText}`)
      }

      return await response.json()
    } catch (error) {
      console.error('Error fetching dental rates:', error)
      throw error
    }
  }
}
